/**
 * Audio service - Sound alert player
 */

class AudioService {
  private audio: HTMLAudioElement | null = null;
  private preloaded: boolean = false;
  private volume: number = 0.5; // Default 50%

  /**
   * Preload the alert sound
   */
  async preloadSound(): Promise<void> {
    if (this.preloaded) return;

    try {
      this.audio = new Audio('/sounds/timer-complete.mp3');
      this.audio.preload = 'auto';

      // Wait for audio to be ready
      await new Promise<void>((resolve, reject) => {
        if (!this.audio) {
          reject(new Error('Audio element not created'));
          return;
        }

        this.audio.addEventListener('canplaythrough', () => resolve(), { once: true });
        this.audio.addEventListener('error', () => reject(new Error('Failed to load audio')), { once: true });
      });

      this.preloaded = true;
      console.log('Alert sound preloaded');
    } catch (error) {
      console.error('Failed to preload alert sound:', error);
      // Try fallback format
      try {
        this.audio = new Audio('/sounds/timer-complete.ogg');
        this.audio.preload = 'auto';
        await new Promise<void>((resolve, reject) => {
          if (!this.audio) {
            reject(new Error('Audio element not created'));
            return;
          }
          this.audio.addEventListener('canplaythrough', () => resolve(), { once: true });
          this.audio.addEventListener('error', () => reject(new Error('Failed to load audio')), { once: true });
        });
        this.preloaded = true;
        console.log('Alert sound preloaded (OGG fallback)');
      } catch (fallbackError) {
        console.error('Failed to preload alert sound (fallback):', fallbackError);
      }
    }
  }

  /**
   * Play the alert sound
   */
  async playAlert(): Promise<void> {
    if (!this.audio) {
      await this.preloadSound();
    }

    if (!this.audio) {
      console.warn('No audio element available');
      return;
    }

    try {
      // Set volume and reset to start
      this.audio.volume = this.volume;
      this.audio.currentTime = 0;

      // Play the sound
      await this.audio.play();
      console.log('Alert sound played');
    } catch (error) {
      console.error('Failed to play alert sound:', error);
    }
  }

  /**
   * Check if audio is supported
   */
  isSupported(): boolean {
    return typeof Audio !== 'undefined';
  }

  /**
   * Set volume (0.0 to 1.0)
   */
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.audio) {
      this.audio.volume = this.volume;
    }
  }

  /**
   * Get current volume (0.0 to 1.0)
   */
  getVolume(): number {
    return this.volume;
  }
}

// Export singleton instance
export const audioService = new AudioService();
export default audioService;
